/* eslint-disable no-unused-vars */
import { ref, reactive, computed, readonly, watch, onMounted, onUnmounted } from 'vue'
import isEqual from 'lodash/isEqual'
import shuffle from 'lodash/shuffle'
import intersection from 'lodash/intersection'
import { useRoute, useRouter } from 'vue-router'

const router = useRouter()
const route = useRoute()

const defaultFilters = () => {
  return {
    tags: new Set(),
    shuffle: false,
    priceSort: 'default', // null|cheapest|mostExpensive
    priceValue: 0,
    datePeriod: 'default', // null|currentMonth|nextMonth
    dateRangeFrom: null,
    dateRangeTo: null
  }
}

// default filter
const DEFAULT_FILTER = defaultFilters()

// default next prev month
const CURRENT_MONTH = getFirstAndLastDayOfMonth('currentMonth')
const NEXT_MONTH = getFirstAndLastDayOfMonth('nextMonth')

// filter storage
const selectedFilters = ref(defaultFilters())

const resetFilters = () => {
  selectedFilters.value = defaultFilters()
}

const areSelectedFiltersEqualDefaults = computed(() => {
  return isEqual(DEFAULT_FILTER, selectedFilters.value)
})

const getPeriodFilter = () => {
  if (!selectedFilters.value.dateRangeFrom && !selectedFilters.value.dateRangeTo) {
    return 'default'
  }

  if (
    selectedFilters.value.dateRangeFrom === CURRENT_MONTH[0] &&
    selectedFilters.value.dateRangeTo === CURRENT_MONTH[1]
  ) {
    return 'currentMonth'
  }

  if (
    selectedFilters.value.dateRangeFrom === NEXT_MONTH[0] &&
    selectedFilters.value.dateRangeTo === NEXT_MONTH[1]
  ) {
    return 'nextMonth'
  }

  return null
}

// 🤡 Tohle je neskutecna klaunovina
// reactive musime obalit tento objekt, protoze jinak v komponentach musime pak pouzivat podobu filtersStorage.priceValue.value
// v jinych playgroundech to jde i bez toho, asi je to nejaky starsi VUE bug u nas ...
const filtersStorage = reactive({
  tags: {
    tagList: computed({
      get: () => selectedFilters.value.tags,
      set: tag => {
        if (filtersStorage.tags.hasTag(tag)) {
          selectedFilters.value.tags.delete(tag)
        } else {
          selectedFilters.value.tags.add(tag)
        }
      }
    }),
    hasTag: tag => selectedFilters.value.tags.has(tag)
  },
  priceValue: computed({
    get: () => selectedFilters.value.priceValue,
    set: v => {
      selectedFilters.value.priceValue = +v // type conversion to number
    }
  }),
  priceSort: computed({
    get: () => selectedFilters.value.priceSort,
    set: v => {
      selectedFilters.value.priceSort = v
    }
  }),
  datePeriod: computed({
    get: () => {
      return getPeriodFilter()
    },
    set: v => {
      selectedFilters.value.datePeriod = v

      if (v === 'default') {
        // reset date ranges
        selectedFilters.value.dateRangeFrom = DEFAULT_FILTER.dateRangeFrom
        selectedFilters.value.dateRangeTo = DEFAULT_FILTER.dateRangeTo
      } else if (v === 'currentMonth') {
        selectedFilters.value.dateRangeFrom = CURRENT_MONTH[0]
        selectedFilters.value.dateRangeTo = CURRENT_MONTH[1]
      } else if (v === 'nextMonth') {
        selectedFilters.value.dateRangeFrom = NEXT_MONTH[0]
        selectedFilters.value.dateRangeTo = NEXT_MONTH[1]
      }
    }
  }),

  dateRangeFrom: computed({
    get: () => selectedFilters.value.dateRangeFrom,
    set: v => {
      if (typeof v === 'string' && !isNaN(new Date(v))) {
        selectedFilters.value.dateRangeFrom = v
      } else {
        selectedFilters.value.dateRangeFrom = DEFAULT_FILTER.dateRangeFrom
      }

      // reset date period
      selectedFilters.value.datePeriod = getPeriodFilter()
    }
  }),
  dateRangeTo: computed({
    get: () => selectedFilters.value.dateRangeTo,
    set: v => {
      if (typeof v === 'string' && !isNaN(new Date(v))) {
        selectedFilters.value.dateRangeTo = v
      } else {
        selectedFilters.value.dateRangeTo = DEFAULT_FILTER.dateRangeTo
      }

      // reset date period
      selectedFilters.value.datePeriod = getPeriodFilter()
    }
  })
})

const filtersReduceCollection = {
  tags: (cards, tags) => {
    if (tags.size === 0) {
      return cards
    }

    return cards.filter(card => {
      const categories = card.categories.map(category => category.id)
      const intersectionDiff = intersection(categories, [...tags])

      return intersectionDiff.length
    })
  },
  priceValue: (cards, price) => {
    if (!price || price === 'default') {
      return cards
    }

    return cards.filter(card => {
      return card?.price?.price <= price || card?.min_price?.price <= price
    })
  },
  dateRange: (cards, range) => {
    if (!(range.from || range.to)) {
      return cards
    }

    const startDate = range.from ? new Date(range.from) : null
    const endDate = range.to ? new Date(range.to) : null

    const cardFiltered = cards.filter(card => {
      const foundedMap = card.workshop_dates.map(dateRow => {
        const targetDate = new Date(dateRow.registration_deadline)

        try {
          return isDateInRange(targetDate, startDate, endDate)
        } catch (error) {
          console.log(error)
          return false
        }
      })

      // rozmezi datumu nebo je to sticky karta nebo kurz nema teminy
      return foundedMap.find(state => state === true) || card.date_not_needed
    })

    return cardFiltered
  }
}

const filtersSortCollection = {
  shuffle: cards => shuffle(cards),
  priceSort: (cards, sort) => {
    switch (sort) {
      case 'cheapest':
        return cards.sort((a, b) => {
          const aPrice = a?.price?.price || a?.min_price?.price
          const bPrice = b?.price?.price || b?.min_price?.price

          return aPrice - bPrice
        })
      case 'mostExpensive':
        return cards.sort((a, b) => {
          const aPrice = a?.price?.price || a?.min_price?.price
          const bPrice = b?.price?.price || b?.min_price?.price

          return bPrice - aPrice
        })
      default:
        return shuffle(cards)
    }
  }
}

const applyReduceSortFilters = cards => {
  /*  reduce */
  // tags
  cards = filtersReduceCollection.tags(cards, selectedFilters.value.tags)

  // price value
  cards = filtersReduceCollection.priceValue(cards, selectedFilters.value.priceValue)

  // date range
  cards = filtersReduceCollection.dateRange(cards, {
    from: selectedFilters.value.dateRangeFrom,
    to: selectedFilters.value.dateRangeTo
  })

  /* sort */
  if (selectedFilters.value.shuffle) {
    cards = filtersSortCollection.shuffle(cards)
  } else if (selectedFilters.value.priceSort) {
    cards = filtersSortCollection.priceSort(cards, selectedFilters.value.priceSort)
  }

  return cards
}

const constructUrl = obj => {
  const params = new URLSearchParams()
  params.append('ambiFilters', 1)

  // Tags
  if (obj.tags.size > 0) {
    params.append('tags', Array.from(obj.tags).join(','))
  }

  // Shuffle
  if (obj.shuffle) {
    params.append('shuffle', '1')
  }

  // Price Sort
  if (obj.priceSort !== 'default') {
    params.append('priceSort', obj.priceSort)
  }

  // Price Value
  if (obj.priceValue !== 0) {
    params.append('priceValue', obj.priceValue.toString())
  }

  // Date Period
  if (obj.datePeriod !== 'default' && obj.datePeriod !== 'null' && obj.datePeriod !== null) {
    params.append('datePeriod', obj.datePeriod)
  }

  // Date Range
  if (obj.dateRangeFrom !== null) {
    params.append('dateRangeFrom', obj.dateRangeFrom)
  }
  if (obj.dateRangeTo !== null) {
    params.append('dateRangeTo', obj.dateRangeTo)
  }

  return params.toString()
}

const deconstructUrl = urlParams => {
  const params = new URLSearchParams(urlParams)
  const obj = defaultFilters()

  // Tags
  if (params.has('tags')) {
    const tags = params.get('tags').split(',')
    tags.forEach(tag => obj.tags.add(+tag))
  }

  // Shuffle
  if (params.has('shuffle')) {
    obj.shuffle = params.get('shuffle') === '1'
  }

  // Price Sort
  if (params.has('priceSort')) {
    obj.priceSort = params.get('priceSort')
  }

  // Price Value
  if (params.has('priceValue')) {
    obj.priceValue = +params.get('priceValue')
  }

  // Date Period
  if (params.has('datePeriod')) {
    obj.datePeriod = params.get('datePeriod')
  }

  // Date Range
  if (params.has('dateRangeFrom')) {
    obj.dateRangeFrom = params.get('dateRangeFrom')
  }
  if (params.has('dateRangeTo')) {
    obj.dateRangeTo = params.get('dateRangeTo')
  }

  return obj
}

/*
 * @param {Date} targetDate - The target date to check.
 * @param {Date} [startDate] - The start date of the range (optional).
 * @param {Date} [endDate] - The end date of the range (optional).
 * @returns {boolean} - True if targetDate is within the range, otherwise false.
 */
function isDateInRange(targetDate, startDate, endDate) {
  // Ensure targetDate is a valid Date object
  if (!(targetDate instanceof Date) || isNaN(targetDate)) {
    throw new Error('targetDate must be a valid Date object')
  }

  // Check if startDate and endDate are valid Date objects
  if (startDate && (!(startDate instanceof Date) || isNaN(startDate))) {
    throw new Error('startDate must be a valid Date object')
  }
  if (endDate && (!(endDate instanceof Date) || isNaN(endDate))) {
    throw new Error('endDate must be a valid Date object')
  }

  // Perform the range check
  if (startDate && endDate) {
    return targetDate >= startDate && targetDate <= endDate
  } else if (startDate) {
    return targetDate >= startDate
  } else if (endDate) {
    return targetDate <= endDate
  } else {
    // If neither startDate nor endDate are provided, return true or false based on your requirement
    return true // or false, depending on your default logic
  }
}

/**
 * Get the first and last day of the current or next month.
 * @param {string} month - The month to get the dates for. Must be either 'currentMonth' or 'nextMonth'.
 * @return {Array<string>} - An array with two strings in the format 'yyyy-mm-dd': the first day and the last day of the selected month.
 */
function getFirstAndLastDayOfMonth(month) {
  let today = new Date()
  let year = today.getFullYear()
  let monthIndex

  if (month === 'currentMonth') {
    monthIndex = today.getMonth()
  } else if (month === 'nextMonth') {
    monthIndex = today.getMonth() + 1
  } else {
    return "Invalid input. Please enter 'currentMonth' or 'nextMonth'."
  }

  let firstDay = new Date(year, monthIndex, 1)
  let lastDay = new Date(year, monthIndex + 1, 0)

  let firstDayFormatted =
    firstDay.getFullYear() +
    '-' +
    (firstDay.getMonth() + 1).toString().padStart(2, '0') +
    '-' +
    firstDay.getDate().toString().padStart(2, '0')
  let lastDayFormatted =
    lastDay.getFullYear() +
    '-' +
    (lastDay.getMonth() + 1).toString().padStart(2, '0') +
    '-' +
    lastDay.getDate().toString().padStart(2, '0')

  return [firstDayFormatted, lastDayFormatted]
}

export default () => {
  const watchUrlChanges = () => {
    if (route && route.hash && route.hash.includes('ambiFilters')) {
      const objectFromUrl = deconstructUrl(route.hash.slice(1))
      selectedFilters.value = objectFromUrl
    
    } else {
      resetFilters()
    }

    const unwatch = watch(
      selectedFilters,
      newValue => {
        if (areSelectedFiltersEqualDefaults.value) {
          if (route.hash !== '') {
            router.replace({ hash: '' })
          }

        } else if(route.hash !== '#' + constructUrl(newValue)) {
          router.replace({ hash: '#' + constructUrl(newValue) })
        }
      },
      {
        deep: true
      }
    )

    onUnmounted(() => {
      unwatch()
    })
  }

  return {
    watchUrlChanges,
    selectedFilters: readonly(selectedFilters),
    filtersStorage,
    areSelectedFiltersEqualDefaults,
    applyReduceSortFilters,
    resetFilters,
    defaultFilters
  }
}
